import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import StatCard from './StatCard';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function WastagePanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');

  const load = async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams();
      if (from) qp.append('from_date', from);
      if (to) qp.append('to_date', to);
      const res = await axios.get(`${API}/insights/wastage?${qp}`, { withCredentials: true });
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  if (loading) return <div className="text-zinc-400 text-sm py-6">Loading wastage…</div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="wastage-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="text-zinc-50 font-heading font-semibold text-sm flex items-center gap-2">
          <Trash2 size={14} className="text-red-400" /> Wastage Audit
        </h3>
        <div className="flex gap-2 ml-auto">
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-zinc-200 text-xs" data-testid="wastage-from-date" />
          <span className="text-zinc-600 text-xs self-center">to</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-zinc-200 text-xs" data-testid="wastage-to-date" />
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Batches Tracked" value={data.total.batches.toLocaleString()} />
        <StatCard label="Expected Output" value={`${data.total.expected_kg.toLocaleString('en-IN')} kg`} />
        <StatCard label="Actual Output" value={`${data.total.actual_kg.toLocaleString('en-IN')} kg`} />
        <StatCard label="Wastage %" value={`${data.total.wastage_pct}%`} accent={data.total.wastage_pct >= 5 ? 'red' : data.total.wastage_pct >= 2 ? 'amber' : 'green'} />
      </div>
      {data.flagged_products?.length > 0 && (
        <div className="bg-red-900/10 border border-red-800 rounded-xl p-4">
          <div className="text-red-300 text-xs font-semibold mb-2">⚠️ {data.flagged_products.length} product(s) with ≥5% wastage</div>
          <div className="flex flex-wrap gap-1.5">
            {data.flagged_products.map(r => (
              <span key={r.product} className="text-xs px-2 py-1 rounded-lg bg-red-900/30 text-red-300 border border-red-700">
                {r.product} · {r.wastage_pct}%
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-4 py-2 border-b border-zinc-800 text-zinc-300 text-xs font-semibold">By Product</div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-500 text-xs sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Product</th>
                  <th className="text-right px-3 py-2">Batches</th>
                  <th className="text-right px-3 py-2">Expected kg</th>
                  <th className="text-right px-3 py-2">Wastage kg</th>
                  <th className="text-right px-3 py-2">Wastage %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {data.by_product.map(r => (
                  <tr key={r.product} className={r.flagged ? 'bg-red-900/10' : ''}>
                    <td className="px-3 py-2 text-zinc-200 text-xs">{r.product}</td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-400 text-xs">{r.batches}</td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-400 text-xs">{r.expected_kg}</td>
                    <td className="px-3 py-2 text-right font-mono text-red-300 text-xs">{r.wastage_kg}</td>
                    <td className={`px-3 py-2 text-right font-mono text-xs ${r.wastage_pct >= 5 ? 'text-red-300 font-semibold' : 'text-zinc-300'}`}>{r.wastage_pct}%</td>
                  </tr>
                ))}
                {data.by_product.length === 0 && <tr><td colSpan={5} className="text-center text-zinc-500 py-6 text-xs">No batch data in this range</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-4 py-2 border-b border-zinc-800 text-zinc-300 text-xs font-semibold">By Month</div>
          <div className="p-4">
            {data.by_month.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.by_month}>
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46' }} />
                  <Bar dataKey="wastage_pct" fill="#EF4444" name="Wastage %" />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="text-center text-zinc-500 py-8 text-xs">No monthly data yet</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
